import React, { useState, useEffect } from "react";
import ProductsTable from "./ProductsTable";
import ProductForm, { Product } from "./ProductForm";
import {
  productService,
  Product as ApiProduct,
} from "../../api/productService";

const ProductsPage: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editProduct, setEditProduct] = useState<Product | undefined>(
    undefined
  );
  const [products, setProducts] = useState<ApiProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const data = await productService.getAllProducts();
      setProducts(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleSave = async (productData: Product) => {
    try {
      if (editProduct?.id) {
        // Update existing product
        await productService.updateProduct(editProduct.id, {
          name: productData.name,
          description: productData.description,
          price: productData.price,
          stock: productData.stock,
          category: productData.category,
          images: productData.images as string[],
          materials: productData.materials,
          size: productData.size,
          idealFor: productData.idealFor,
          isFeaturedProduct: productData.isFeaturedProduct,
          enableMaterialType: productData.enableMaterialType,
        } as any);
      } else {
        await productService.createProduct({
          name: productData.name,
          description: productData.description,
          price: productData.price,
          stock: productData.stock,
          category: productData.category,
          images: productData.images as string[],
          materials: productData.materials,
          size: productData.size,
          idealFor: productData.idealFor,
          isFeaturedProduct: productData.isFeaturedProduct,
          enableMaterialType: productData.enableMaterialType,
        } as any);
      }
      await fetchProducts();
      setShowForm(false);
      setEditProduct(undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save product");
    }
  };

  const handleDelete = async (productId: string) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        await productService.deleteProduct(productId);
        await fetchProducts();
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to delete product"
        );
      }
    }
  };

  if (loading) {
    return <div>Loading products...</div>;
  }

  if (error) {
    return <div style={{ color: "red" }}>Error: {error}</div>;
  }

  return (
    <div>
      {!showForm ? (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 24,
            }}
          >
            <h2>Products ({products.length})</h2>
            <button
              onClick={() => {
                setEditProduct(undefined);
                setShowForm(true);
              }}
              style={{
                background: "#e97d7d",
                color: "#fff",
                border: "none",
                borderRadius: "2rem",
                padding: "0.7rem 1.5rem",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Add New Product
            </button>
          </div>
          <ProductsTable
            products={products}
            onEdit={(product) => {
              const formProduct: Product = {
                id: product.id,
                name: product.name,
                description: product.description,
                price: product.price,
                stock: product.stock,
                category: product.category,
                images: product.images,
                materials: (product as any).materials,
                size: (product as any).size,
                idealFor: (product as any).idealFor,
                isFeaturedProduct: (product as any).isFeaturedProduct,
                enableMaterialType: (product as any).enableMaterialType,
              };
              setEditProduct(formProduct);
              setShowForm(true);
            }}
            onDelete={handleDelete}
          />
        </>
      ) : (
        <ProductForm
          product={editProduct}
          onSave={handleSave}
          onCancel={() => {
            setShowForm(false);
            setEditProduct(undefined);
          }}
        />
      )}
    </div>
  );
};
export default ProductsPage;
